import React from 'react';
import TextProcessor from "@/components/common/TextProcessor";
import clsx from "clsx";
import Image from "next/image";
import { type Block } from "@/sections/SectionRenderer";
import { ArrowRightIcon } from "@heroicons/react/24/solid";

interface FlexibleBannerButton {
  label: string;
  href: string;
}

interface FlexibleBannerData extends Block {
  name: 'flexible-banner';
  title?: string;
  subtitle?: string;
  description?: string;
  reverse?: boolean;
  dark?: boolean;
  button?: FlexibleBannerButton;
  image: {
    light: {
      src: string;
      className?: string;
      style?: object;
    }
    dark: {
      src: string;
      className?: string;
      style?: object;
    }
  }
}

export const FlexibleBanner: React.FC<{ data: FlexibleBannerData }> = ({ data }) => {
  const {
    id,
    title,
    subtitle,
    description,
    button,
    image,
    reverse = false,
  } = data;
  
  // Use dark: variants instead of runtime branching to avoid first-load mismatch
  const bgClass = "bg-crystal-bell dark:bg-eerie-black";
  const textClass = "text-eerie-black dark:text-custom-white";
  // Dual shadow variants (keep both so CSS handles theme instantly)
  const shadowLtr = "shadow-crystal-bell-fade-ltr dark:shadow-eerie-black-fade-ltr";
  const shadowRtl = "shadow-crystal-bell-fade-rtl dark:shadow-eerie-black-fade-rtl";

   return (
     <section id={id} className={clsx("py-4 md:py-8 lg:py-12")}>
       <div
         className={[
           bgClass,
           "p-0 min-h-[400px] md:min-h-[500px] lg:min-h-[600px]",
           "mx-6 lg:mx-32 flex flex-col md:flex-row",
           reverse ? "md:flex-row-reverse" : "",
           "items-stretch rounded-2xl overflow-hidden z-10"
         ].join(" ")}
       >
         {/* Image container */}
         <div
           className={[
             "relative w-full h-56 sm:h-72 md:flex-1 md:h-auto",
             "overflow-hidden md:overflow-visible"
           ].join(" ")}
         >
          {/* Light image */}
          <div className="absolute inset-0 dark:opacity-0">
            <Image
              src={image.light.src}
              alt={title ?? subtitle ?? 'Flexible banner'}
              priority
              fill
              quality={100}
              unoptimized
              sizes="(max-width: 768px) 100vw, 50vw"
              className={clsx("w-full h-full object-cover object-center", image.light.className)}
              style={image.light.style}
            />
          </div>
          {/* Dark image */}
          <div className="absolute inset-0 opacity-0 dark:opacity-100">
            <Image
              src={image.dark.src}
              alt={title ?? subtitle ?? 'Flexible banner'}
              priority
              fill
              quality={100}
              unoptimized
              sizes="(max-width: 768px) 100vw, 50vw"
              className={clsx("w-full h-full object-cover object-center", image.dark.className)}
              style={image.dark.style}
            />
          </div>
         </div>
         
         {/* Content container */}
         <div
          className={clsx(
            "z-10 md:flex-1 flex flex-col justify-center px-4 sm:px-6 md:px-8",
            reverse
              ? `md:pr-8 lg:pr-12 ${shadowLtr}`
              : `${shadowRtl} md:pl-8 lg:pl-12`,
            "py-8 md:py-12"
          )}>
           {title && (
             <h3
               className={`${textClass} text-sm font-bold mb-2 md:mb-4 flex flex-row items-center space-x-2`}>
               <div className="w-2 h-2 bg-accent rounded-full" />
               <div>{title}</div>
             </h3>
           )}
           {subtitle && (
             <h2
               className={`${textClass} text-2xl sm:text-3xl md:text-4xl mb-4 md:mb-6 lg:mb-8`}>
               <TextProcessor>{subtitle}</TextProcessor>
             </h2>
           )}
           {description && (
             <p
               className={`${textClass} text-base sm:text-lg md:text-xl font-semibold mt-4 md:mt-6 lg:mt-8 text-justify`}>
               <TextProcessor>{description}</TextProcessor>
             </p>
           )}
           {/* Button */}
           {button && (
             <div className="mt-8 md:mt-10 flex justify-end">
               <a
                 href={button.href}
                 className={clsx(
                   "inline-flex items-center rounded-md px-5 py-3 font-bold shadow-lg",
                   "bg-meteor-shower hover:bg-venetian-night hover:shadow-xl",
                   "text-white",
                   "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-meteor-shower",
                   "transition-colors transition-shadow duration-200"
                 )}
               >
                 {button.label}
                 <ArrowRightIcon className="ml-2 w-5 h-5 text-white" aria-hidden="true" />
               </a>
             </div>
           )}
         </div>
       </div>
     </section>
   );
};
